// Refactored from https://github.com/Azure-Samples/ms-identity-javascript-react-tutorial/blob/main/1-Authentication/1-sign-in/SPA/src/authConfig.js

import { IPublicClientApplication } from "@azure/msal-browser";

const appServicesAuthTokenUrl = ".auth/me";
const appServicesAuthTokenRefreshUrl = ".auth/refresh";
const appServicesAuthLogoutUrl = ".auth/logout?post_logout_redirect_uri=/";

interface AppServicesToken {
    id_token: string;
    access_token: string;
    user_claims: Record<string, any>;
    expires_on: string;
}

interface IdentityProvider {
    loginUrl: string;
    enabled: boolean;
}

interface AuthSetup {
    // Set to true if login elements should be shown in the UI
    useLogin: boolean;
    // Set to true if access control is enforced by the application
    requireAccessControl: boolean;
    // Set to true if the application allows unauthenticated access (only applies for documents without access control)
    enableUnauthenticatedAccess: boolean;
    /**
     * Configuration object to be passed to MSAL instance on creation.
     * For a full list of MSAL.js configuration parameters, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
     */
    msalConfig: {
        auth: {
            clientId: string; // Client app id used for login
            authority: string; // Directory to use for login https://learn.microsoft.com/entra/identity-platform/msal-client-application-configuration#authority
            redirectUri: string; // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
            postLogoutRedirectUri: string; // Indicates the page to navigate after logout.
            navigateToLoginRequestUrl: boolean; // If "true", will navigate back to the original request location before processing the auth code response.
        };
        cache: {
            cacheLocation: string; // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
            storeAuthStateInCookie: boolean; // Set this to "true" if you are having issues on IE11 or Edge
        };
    };
    loginRequest: {
        /**
         * Scopes you add here will be prompted for user consent during sign-in.
         * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
         * For more information about OIDC scopes, visit:
         * https://learn.microsoft.com/entra/identity-platform/permissions-consent-overview#openid-connect-scopes
         */
        scopes: Array<string>;
    };
    tokenRequest: {
        scopes: Array<string>;
    };
    // Identity providers available via Easy Auth
    identityProviders?: {
        microsoft: IdentityProvider;
        salesforce: IdentityProvider;
    };
}

// Fetch the auth setup JSON data from the API if not already cached
async function fetchAuthSetup(): Promise<AuthSetup> {
    //return  {}
    const response = await fetch("/auth_setup");
    if (!response.ok) {
        throw new Error(`auth setup response was not ok: ${response.status}`);
    }
    return await response.json();
}

const authSetup = await fetchAuthSetup();

export const useLogin = authSetup.useLogin;

export const requireAccessControl = authSetup.requireAccessControl;

export const enableUnauthenticatedAccess = authSetup.enableUnauthenticatedAccess;

export const requireLogin = requireAccessControl && !enableUnauthenticatedAccess;

// Identity providers for the login selection page.
// Microsoft goes through Easy Auth. Salesforce uses our custom OAuth flow
// (bypassing Easy Auth) so we can request the `api` scope correctly.
const backendProviders = authSetup.identityProviders ?? {
    microsoft: { loginUrl: "/.auth/login/aad", enabled: true },
    salesforce: { loginUrl: "/.auth/login/salesforce", enabled: true },
};
export const identityProviders = {
    ...backendProviders,
    // Override: point Salesforce at our custom route, not Easy Auth.
    salesforce: { loginUrl: "/api/auth/salesforce/login", enabled: true },
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = authSetup.msalConfig;

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://learn.microsoft.com/entra/identity-platform/permissions-consent-overview#openid-connect-scopes
 */
export const loginRequest = authSetup.loginRequest;

const tokenRequest = authSetup.tokenRequest;

// Build an absolute redirect URI using the current window's location and the relative redirect URI from auth setup
export const getRedirectUri = () => {
    return window.location.origin + authSetup.msalConfig.auth.redirectUri;
};

// Cache the app services token if it's available
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/this#global_context
declare global {
    var cachedAppServicesToken: AppServicesToken | null;
}
globalThis.cachedAppServicesToken = null;

/**
 * Retrieves an access token if the user is logged in using app services authentication.
 * Checks if the current token is expired and fetches a new token if necessary.
 * Returns null if the app doesn't support app services authentication.
 *
 * @returns {Promise<AppServicesToken | null>} A promise that resolves to an AppServicesToken if the user is authenticated, or null if authentication is not supported or fails.
 */
const getAppServicesToken = (): Promise<AppServicesToken | null> => {
    const checkNotExpired = (appServicesToken: AppServicesToken) => {
        // Custom OIDC providers (e.g. Salesforce) may not return expires_on
        if (!appServicesToken.expires_on) return true;
        const currentDate = new Date();
        const expiresOnDate = new Date(appServicesToken.expires_on);
        // If the date is invalid, treat as not expired (token exists = authenticated)
        if (isNaN(expiresOnDate.getTime())) return true;
        return expiresOnDate > currentDate;
    };
    
    if (globalThis.cachedAppServicesToken && checkNotExpired(globalThis.cachedAppServicesToken)) {
        return Promise.resolve(globalThis.cachedAppServicesToken);
    }

    const getAppServicesTokenFromMe: () => Promise<AppServicesToken | null> = () => {
        return fetch(appServicesAuthTokenUrl).then(r => {
            if (r.ok) {
                return r.json().then(json => {
                    if (json.length > 0) {
                        return {
                            id_token: json[0]["id_token"] as string,
                            access_token: json[0]["access_token"] as string,
                            user_claims: json[0]["user_claims"].reduce((acc: Record<string, any>, item: Record<string, any>) => {
                                acc[item.typ] = item.val;
                                return acc;
                            }, {}) as Record<string, any>,
                            expires_on: json[0]["expires_on"] as string
                        } as AppServicesToken;
                    }

                    return null;
                });
            }

            return null;
        });
    };
    

    // Also try our custom Salesforce OAuth session endpoint.
    const getSalesforceCustomSession: () => Promise<AppServicesToken | null> = () => {
        return fetch("/api/auth/salesforce/me", { credentials: "include" }).then(r => {
            if (!r.ok) return null;
            return r.json().then(json => {
                if (!json.authenticated) return null;
                // Shape it like an AppServicesToken so the rest of the app works unchanged.
                // The actual access_token stays on the server (in the session cookie) -
                // we don't need it on the frontend.
                return {
                    id_token: "",
                    access_token: "",
                    user_claims: json.claims || {},
                    expires_on: "",
                } as AppServicesToken;
            });
        }).catch(() => null);
    };

    return getAppServicesTokenFromMe().then(token => {
        if (token) {
            if (checkNotExpired(token)) {
                globalThis.cachedAppServicesToken = token;
                return token;
            }

            return fetch(appServicesAuthTokenRefreshUrl).then(r => {
                if (r.ok) {
                    return getAppServicesTokenFromMe();
                }
                return null;
            });
        }

        // Fall back to our custom Salesforce session (for users who logged in
        // via /api/auth/salesforce/login instead of Easy Auth).
        return getSalesforceCustomSession().then(sfToken => {
            if (sfToken) {
                globalThis.cachedAppServicesToken = sfToken;
            }
            return sfToken;
        });
    });
};

export const isUsingAppServicesLogin = (await getAppServicesToken()) != null;

// Sign out of app services
// Learn more at https://learn.microsoft.com/azure/app-service/configure-authentication-customize-sign-in-out#sign-out-of-a-session
export const appServicesLogout = () => {
    window.location.href = appServicesAuthLogoutUrl;
};

/**
 * Determines if the user is logged in either via the MSAL public client application or the app services login.
 * @param {IPublicClientApplication | undefined} client - The MSAL public client application instance, or undefined if not available.
 * @returns {Promise<boolean>} A promise that resolves to true if the user is logged in, false otherwise.
 */
export const checkLoggedIn = async (client: IPublicClientApplication | undefined): Promise<boolean> => {
    if (client) {
        const activeAccount = client.getActiveAccount();
        if (activeAccount) {
            return true;
        }
    }

    const appServicesToken = await getAppServicesToken();
    if (appServicesToken) {
        return true;
    }

    return false;
};

// Get the best token for use with the API server.
// For Microsoft Entra, the access_token is a JWT. For Salesforce OIDC, the access_token
// is opaque, so we use the id_token instead (which is a proper JWT).
export const getToken = async (client: IPublicClientApplication): Promise<string | undefined> => {
    const appServicesToken = await getAppServicesToken();
    if (appServicesToken) {
        // Prefer access_token if it's a JWT, otherwise fall back to id_token
        const token = appServicesToken.access_token;
        if (token && token.split(".").length === 3) {
            return token;
        }
        return appServicesToken.id_token ?? token;
    }

    return client
        .acquireTokenSilent({
            ...tokenRequest,
            redirectUri: getRedirectUri()
        })
        .then(r => r.accessToken)
        .catch(error => {
            console.error("MSAL token error:", error);
            return undefined;
        });
};

export const getIdToken = async (): Promise<string | undefined> => {
    const appServicesToken = await getAppServicesToken();
    if (appServicesToken) {
        return appServicesToken.id_token;
    }

    // Fallback: you could optionally try MSAL if needed
    return undefined;
};

/**
 * Retrieves the username of the active account.
 * If no active account is found, attempts to retrieve the username from the app services login token if available.
 * @param {IPublicClientApplication} client - The MSAL public client application instance.
 * @returns {Promise<string | null>} The username of the active account, or null if no username is found.
 */
export const getUsername = async (client: IPublicClientApplication): Promise<string | null> => {
    const activeAccount = client.getActiveAccount();
    if (activeAccount) {
        return activeAccount.username;
    }

    const appServicesToken = await getAppServicesToken();
    if (appServicesToken?.user_claims) {
        // Entra uses preferred_username, Salesforce uses email or name
        return appServicesToken.user_claims.preferred_username
            || appServicesToken.user_claims.email
            || appServicesToken.user_claims.name
            || null;
    }

    return null;
};

/**
 * Fetches the raw /.auth/me endpoint and extracts all group claims (`typ: "groups"`).
 * This bypasses the reduction logic in getAppServicesToken, which collapses multiple group entries.
 * Useful for checking if a user belongs to specific Azure AD groups.
 *
 * @returns A Promise that resolves to an array of group IDs (strings).
 */
export const getAllUserGroups = async (): Promise<string[]> => {

    try {
        const response = await fetch(appServicesAuthTokenUrl);
        if (!response.ok) {
            console.warn("⚠️ Failed to fetch /.auth/me. Status:", response.status);
            return [];
        }

        const json = await response.json();
        if (json.length === 0 || !json[0]["user_claims"]) {
            console.warn("⚠️ No user claims found in the /.auth/me response.");
            return [];
        }

        const claims = json[0]["user_claims"];

        const groupClaims = claims
            .filter((claim: Record<string, any>) => claim.typ === "groups")
            .map((claim: Record<string, any>) => claim.val);

        return groupClaims;
    } catch (err) {
        console.error("❌ Error while fetching user group claims:", err);
        return [];
    }
};


/**
 * Retrieves the token claims of the active account.
 * If no active account is found, attempts to retrieve the token claims from the app services login token if available.
 * @param {IPublicClientApplication} client - The MSAL public client application instance.
 * @returns {Promise<Record<string, unknown> | undefined>} A promise that resolves to the token claims of the active account, the user claims from the app services login token, or undefined if no claims are found.
 */
export const getTokenClaims = async (
    client?: IPublicClientApplication
): Promise<Record<string, unknown> | undefined> => {
    const activeAccount = client?.getActiveAccount(); // ✅ now safe
    if (activeAccount) {
        return activeAccount.idTokenClaims;
    }

        const appServicesToken = await getAppServicesToken(); // Easy Auth fallback

        if (appServicesToken) {
            return appServicesToken.user_claims;
        }

    return undefined;
};


